import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToggle } from 'usehooks-ts';

import { useGetCurrentUserQuery } from '@app/store/slices/users/usersApi';
import {
  telegramShareInviteMessageWithInitialReward,
  telegramVibrate,
} from '@app/shared/utils/common';
import { Page } from '@app/routes';
import type { HomeBanner } from '@app/shared/types/pages/home';
import {
  airDropImage,
  introduceImage,
  meetImage,
  payImage,
  presaleImage,
  roadmapImage,
  villagesImage,
  whatIsDomImage,
  S3_HOST,
} from '@app/assets';

export const useCognitionCarousel = () => {
  const { data: user } = useGetCurrentUserQuery();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVideoHidden, toggleVideoHidden] = useToggle(true);

  const banners: HomeBanner[] = [
    {
      variant: 'text',
      img: introduceImage,
      text: {
        title: 'home.slider.introduce.title',
        description: 'home.slider.introduce.description',
      },
      button: {
        text: 'common.open',
        onClick: () => user && telegramShareInviteMessageWithInitialReward(user, t),
      },
    },
    {
      variant: 'placeholder',
      img: airDropImage,
      placeholder: 'AirDrop\n Cookie coin',
      button: { text: 'common.learn_more', onClick: () => navigate(Page.ROADMAP) },
    },
    {
      variant: 'text',
      img: villagesImage,
      text: { title: 'home.slider.village.title', description: 'home.slider.village.description' },
    },
    {
      variant: 'text',
      img: presaleImage,
      button: { text: 'bottom_navigations.shop', onClick: () => navigate(Page.COOKIES_SHOP) },
    },
    { variant: 'what_is_dom', img: whatIsDomImage },
    {
      variant: 'text',
      img: payImage,
      text: { title: 'home.slider.pay.title', description: 'home.slider.pay.description' },
      soon: true,
    },
    {
      variant: 'text',
      img: meetImage,
      text: { title: 'home.slider.meet.title', description: 'home.slider.meet.description' },
      soon: true,
    },
    {
      variant: 'text',
      img: roadmapImage,
      text: { title: 'home.slider.roadmap.title' },
      button: {
        text: 'common.open',
        onClick: () => window.open(`${S3_HOST}/presentations/DOM_INFO.pdf`, '_blank'),
      },
    },
  ];

  const handleSlideChange = (index: number) => {
    telegramVibrate();
    setCurrentIndex(index);
  };

  return { banners, currentIndex, handleSlideChange, isVideoHidden, toggleVideoHidden };
};
